import React, { useState } from 'react';
import { useSuspenseQuery } from '@apollo/client/react';
import {
  NarvarStatus,
  Vehicle,
  NarvarStatusCode,
  Query,
  GetTranslationsQueryVariables,
} from '../../../../../../__generated__/loma';
import { LayoutTree } from '../../DynamicLayout';
import { MediumHeader } from '../../../ui/Header/Header';
import {
  NarvarClickSubTypes,
  NarvarEventComponents,
  NarvarEventTypes,
  useAnalyticsPost,
} from '../../../../hooks/Analytics';
import { useTheme } from '../../../../hooks/useTheme';
import { usePageContext } from '../../../../hooks/PageContext/usePageContext';
import translationsQueryByKey from '../../../../../../graphql/translationsQueryByKey';
import Action from './Action';
import useIsMobile from '../hooks/useIsMobile';
import { Call, Chat } from './Icons';
import styles from './Details.module.css';

interface DetailsProps extends Vehicle {
  status: NarvarStatus;
  layout?: LayoutTree.FeatureLayout;
  featureName?: string;
  statusCode?: NarvarStatusCode;
  subStatus?: NarvarStatusCode | null;
}

const Details = (props: DetailsProps) => {
  const isMobile = useIsMobile();
  const logEvent = useAnalyticsPost();
  const [showNumber, setShowNumber] = useState(false);

  const {
    driver,
    type,
    color,
    status,
    layout,
    featureName,
    statusCode,
    subStatus,
  } = props;

  const { state } = usePageContext();
  const {
    retailerMoniker,
    localeLanguage,
    localeCountry,
    versionName,
    type: trackingType,
    cache,
  } = state;

  const stringifiedKeys = [
    'driverDetailsCallButtonText',
    'driverDetailsChatButtonText',
  ].join('\n');

  const { data: translationsData } = useSuspenseQuery<
    Pick<Query, 'retailerTranslations'>,
    GetTranslationsQueryVariables
  >(translationsQueryByKey(stringifiedKeys), {
    skip: !statusCode && !status,
    variables: {
      retailerMoniker,
      localeLanguage: localeLanguage || 'en',
      localeCountry,
      versionName,
      type: trackingType,
      category: subStatus ?? statusCode,
      status,
      cache,
    },
  });

  const { driverDetailsCallButtonText, driverDetailsChatButtonText } =
    translationsData?.retailerTranslations ?? {};

  const { bodytextDefaultNone, iconsPrimaryNone } = useTheme();

  if (!driver) return <></>;

  const number = driver.contact ? driver.contact.proxyPhoneNumber : '';

  const onChat = () => {
    logEvent({
      eventType: NarvarEventTypes.CLICK,
      clickCategory: NarvarClickSubTypes.TEXTING_DRIVER,
      clickSubType: NarvarClickSubTypes.TEXTING_DRIVER,
      eventComponent: NarvarEventComponents.NTH_BLOCK_DRIVER_DETAILS,
      eventFeature: NarvarEventComponents.NTH_BLOCK_DRIVER_DETAILS,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_BLOCK_DRIVER_DETAILS,
    });

    if (isMobile) window.open(`sms:${number ?? ''}`);
    else setShowNumber(true);
  };

  const onCall = () => {
    logEvent({
      eventType: NarvarEventTypes.CLICK,
      clickCategory: NarvarClickSubTypes.CALLING_DRIVER,
      clickSubType: NarvarClickSubTypes.CALLING_DRIVER,
      eventComponent: NarvarEventComponents.NTH_BLOCK_DRIVER_DETAILS,
      eventFeature: NarvarEventComponents.NTH_BLOCK_DRIVER_DETAILS,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_BLOCK_DRIVER_DETAILS,
    });

    if (isMobile) window.open(`tel:${number ?? ''}`, '_top');
    else setShowNumber(true);
  };

  const showContact = status !== 'LDDELIVERED' && status !== 'HPUPICKEDUP';

  if (!driver.name && !number) return <></>;

  const isLandscape = layout === LayoutTree.FeatureLayout.LANDSCAPE;

  return (
    <div className={isLandscape ? styles.containerLandscape : styles.container}>
      <div className={styles.infoContainer}>
        {driver.photo && (
          <img className={styles.avatar} src={driver.photo} alt="" />
        )}
        <div className={styles.textContainer}>
          {driver.name && <MediumHeader as="h2">{driver.name}</MediumHeader>}
          {color && type && (
            <span style={{ ...bodytextDefaultNone }}>{`${color} ${type}`}</span>
          )}
        </div>
      </div>
      {showContact && layout === LayoutTree.FeatureLayout.PORTRAIT && (
        <div style={{ height: '24px' }} />
      )}
      {showContact && number && (
        <div className={styles.contactContainer}>
          {showNumber ? (
            <p className={styles.number}>{number}</p>
          ) : (
            <>
              <Action
                icon={<Chat inlineStyle={iconsPrimaryNone} />}
                text={driverDetailsChatButtonText}
                onClick={onChat}
              />
              <Action
                icon={<Call inlineStyle={iconsPrimaryNone} />}
                text={driverDetailsCallButtonText}
                onClick={onCall}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Details;
